/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import ReactTooltip from "react-tooltip";
import { DeleteExternalUser } from "../../api/ExternalUser";
import useClickOutside from "../../hook/useClickOutside";
import { customTooltip, Flex, notSelect } from "../../styles/general";
import { IExternalUser } from "../../types/ExternalUserTypes";
import IconBerserkerMail from "../Icon";
import { useModal } from "../Modal/Modal";
import Warning from "./../Modal/container/Warning";

const Container = styled.div`
  position: relative;
  margin-left: 5px;
  color: #2f2f2f;
  ${notSelect}
`;

interface PopoverProps {
  bottomPopover: boolean;
}

const Popover = styled.div<PopoverProps>`
  position: absolute;
  background: white;
  z-index: 20;
  top: ${(props) => (props.bottomPopover ? "30px" : "auto")};
  bottom: ${(props) => (props.bottomPopover ? "auto" : "30px")};

  height: fit-content;
  width: 240px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  /* left: 0; */
  right: 1px;
  display: flex;
  flex-direction: column;
  padding: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;
  }
`;

const Icon = styled.img`
  width: 15px;
  height: 15px;
  cursor: pointer;
`;

const OptionIcon = styled.img`
  width: 15px;
  height: 15px;
  margin-right: 5px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 15px;
`;

interface Props {
  externalUser: IExternalUser;
  updateUsers: () => void;
}

export default function MoreActionsPopOver(props: Props): JSX.Element {
  const { externalUser, updateUsers } = props;
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [bottomPopover, setBottomPopover] = useState(true);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const modal = useModal();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let bottom = containerRef.current.getBoundingClientRect().bottom;

    if (!bottomPopover)
      bottom =
        bottom + containerRef.current.getBoundingClientRect().height + 20;

    setBottomPopover(bottom >= 0 && bottom <= window.innerHeight);
  }, [containerRef, openDropView]);

  return (
    <Container ref={wrapperRef}>
      <Flex
        style={{ height: "30px", cursor: "pointer" }}
        data-tip
        data-for="moreActions"
        onClick={() => setOpenDropView(!openDropView)}
      >
        <ReactTooltip
          id="moreActions"
          place="bottom"
          effect="solid"
          globalEventOff="click"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText>More Actions</TooltipText>
        </ReactTooltip>

        <div
          css={css`
            cursor: pointer;
          `}
          onClick={() => setOpenDropView(!openDropView)}
        >
          <IconBerserkerMail name="threedots" />
        </div>
      </Flex>

      {openDropView && (
        <Popover ref={containerRef} bottomPopover={bottomPopover}>
          <Title>ACTIONS</Title>

          <Row
            onClick={() => {
              modal.pushModal(
                <Warning
                  imageSize="lg"
                  icon={"./icons/Construction.svg"}
                  title="Coming soon"
                  description="This feature isn't ready yet. We are working to get it done, and soon it will be here for you to use."
                  buttonsName={["Ok"]}
                  onOkClicked={() => {
                    modal.popModal();
                  }}
                  onCancelClicked={() => {
                    modal.popModal();
                  }}
                />
              );
            }}
          >
            <FaEdit style={{ marginRight: "6px" }} />
            <span>Edit user details</span>
          </Row>
          <Row
            onClick={() => {
              modal.pushModal(
                <Warning
                  icon={"./icons/delete.svg"}
                  title="Delete User"
                  description="Are you sure you want to delete this user?"
                  buttonsName={["No", "Yes"]}
                  onOkClicked={async () => {
                    try {
                      await DeleteExternalUser(externalUser._id);
                      updateUsers();
                      modal.popModal();
                    } catch (e) {
                      modal.popModal();
                      console.error(e);
                    }
                  }}
                  onCancelClicked={() => modal.popModal()}
                />
              );
            }}
          >
            <FaTrashAlt style={{ marginRight: "6px" }} />
            <span>Delete user</span>
          </Row>
        </Popover>
      )}
    </Container>
  );
}
